import styled from '@emotion/styled';
import { Input } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/shared/components/Button';
import { color, font } from 'src/shared/styles/variables';

const supportWithSpecialtiesPills = [
    'ADHD',
    'Anxiety',
    'Autism',
    'Depression',
    'Eating Disorder',
    'Grief',
    'PostPartum',
    'OCD',
    'Substance Use',
    'Trauma',
    'Health Psychology',
    'Child Psychology',
    'Couples Counseling',
    'Family Therapy',
    'Other',
];

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    maxHeight: 1000,
});

const Title = styled.h3({
    textAlign: 'center',
    color: color.pickledBluewood,
    ...font.black(font.size.headerExtended),
});

const Description = styled.h5({
    textAlign: 'center',
    color: color.steelGray,
    ...font.book(font.size.large),
});

const TilesWrapper = styled.ul({
    margin: 0,
    padding: 0,
    maxWidth: 670,
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
});

interface TileProps {
    selected: boolean;
}

const Tile = styled.li<TileProps>(props => ({
    marginTop: 12,
    width: '32%',
    textAlign: 'center',
    padding: '15px 0',
    opacity: props.selected ? 1 : 0.6,
    borderRadius: 6,
    backgroundColor: props.selected ? color.royalBlue : color.catskillWhite,
    color: props.selected ? color.white : color.blueBayoux,
    transition: '.3s all',

    ':hover': {
        cursor: 'pointer',
        opacity: 1,
    },
    ...font.heavy(font.size.base),
}));

const TextArea = styled(Input.TextArea)({
    '&&': {
        maxWidth: 670,
        marginTop: 16,
        padding: 11,
        border: `2px solid ${color.linkWaterLight}`,
        color: color.blueBayoux,
        ...font.medium(font.size.base),
    },
});

const ButtonsWrapper = styled.div({
    display: 'flex',
    marginTop: 40,
    width: '100%',
    maxWidth: 500,
    justifyContent: 'space-between',
});

export enum TherapySupportWithTestId {
    Continue = 'TherapySupportWithTestId.Continue',
    Previous = 'TherapySupportWithTestId.Previous',
}

interface Props {
    supportWithSpecialties: string[];
    supportWithOtherSpecialties: string;
    onTilePress: (text: string) => void;
    onTextAreaChange: (text: string) => void;
    onPreviousPress: () => void;
    onContinuePress: () => void;
}

export const TherapySupportWith: React.FC<Props> = ({
    supportWithOtherSpecialties,
    supportWithSpecialties,
    onTilePress,
    onTextAreaChange,
    onPreviousPress,
    onContinuePress,
}) => {
    const { t } = useTranslation();
    const dataValid = supportWithSpecialties.includes('Other')
        ? supportWithOtherSpecialties.trim() != ''
        : supportWithSpecialties.length > 0;

    return (
        <Wrapper>
            <Title>{t('providers:therapy_support.title')}</Title>
            <Description>{t('providers:therapy_support.description')}</Description>

            <TilesWrapper>
                {supportWithSpecialtiesPills.map(specialty => {
                    const specialtyKey = specialty.replace(' ', '_').toLowerCase();
                    return (
                        <Tile
                            key={specialty}
                            selected={supportWithSpecialties.includes(specialty)}
                            onClick={() => onTilePress(specialty)}
                        >
                            {t(`providers:therapy_support.specialty.${specialtyKey}`)}
                        </Tile>
                    );
                })}
            </TilesWrapper>

            {supportWithSpecialties.includes('Other') ? (
                <TextArea
                    rows={2}
                    placeholder={t('providers:therapy_support.help')}
                    value={supportWithOtherSpecialties}
                    onChange={({ target }) => onTextAreaChange(target.value)}
                />
            ) : null}

            <ButtonsWrapper>
                <Button
                    testID={TherapySupportWithTestId.Previous}
                    onClick={onPreviousPress}
                    type="cancel"
                    widthPercentage={48}
                >
                    {t('common.previous')}
                </Button>
                <Button
                    testID={TherapySupportWithTestId.Continue}
                    onClick={onContinuePress}
                    type="accept"
                    widthPercentage={48}
                    withShadow={dataValid}
                    disabled={!dataValid}
                >
                    {t('common.continue')}
                </Button>
            </ButtonsWrapper>
        </Wrapper>
    );
};
